import React, { useState, useRef } from 'react-x';
import propTypes from 'prop-types-x';
import { animationFrame } from '../../utilities/animationFrame.js';
import { publish } from '../../utilities/events.js';
import { useUpdateEffect } from '../hooks/useUpdateEffect.js';
import { LazyCCISImage } from './LazyCCISImage.jsx';
import { fireGTMTrackingEvent } from '../../utilities/fireGTMTrackingEvent.js';
import { CORE_GTM_TRACKING_VALUES } from '../../constants/analytics.js';
import { RANGE_GTM_TRACKING_VALUES } from '../../../../../src/client/range/ui/constants/analytics.js';

export function ImageRotator(props) {

  const imgViewingAngleRef = useRef(props.imgConfig.angle);
  const imgRotationDirectionRef = useRef(props.imgRotationDirection);
  const imgRotationTimerRef = useRef(null);
  const imgInitPropsRef = useRef(props.imgConfig);
  const imgPropsRef = useRef(props.imgConfig);
  const downRef = useRef(false);
  const downStartPointRef = useRef(0);
  const resetInProgressRef = useRef(false);
  const resetRotateDegreesRef = useRef(0);
  const [angleOffset, setAngleOffset] = useState(0);
  const imgRotationDegrees = props.imgRotationDegrees || 10; // increments of 10 only
  const imgRotateInterval = props.imgRotationSpeed || 0; // 0 is fastest
  const block = 'c-image-rotator';

  // The angle prop value inimgPropsRef gets update when rototating the image.
  // In order to know when a new image (diff color, accessories etc) is being loaded (not a chnage in angle)
  // we need to check the imgProps prop against the initial images Props and not the updates angle one.
  if (props.imgConfig !== imgInitPropsRef.current) {
    imgInitPropsRef.current = props.imgConfig;
    imgPropsRef.current = Object.assign({}, props.imgConfig, { angle: imgPropsRef.current.angle }); // when changing options retain current viewing angle
  }

  useUpdateEffect(() => {
    const rotationDegrees = resetInProgressRef.current ? resetRotateDegreesRef.current : imgRotationDegrees;
    if (Math.abs(angleOffset) <= rotationDegrees/10 && angleOffset !== 0) {
      imgRotationTimerRef.current = setTimeout(() => {
        animationFrame(() => {
          imgPropsRef.current = getNextImageProps(imgRotationDirectionRef.current);
          setAngleOffset(prevAngleOffset => 
            imgRotationDirectionRef.current === 'clockwise' ? 
              prevAngleOffset + 1 : 
              prevAngleOffset - 1
            );
        })
      }, imgRotateInterval);
    } else {
      clearTimeout(imgRotationTimerRef.current);
      resetInProgressRef.current = false;
    }
  }, [angleOffset]);

  // Update when on triggered via prop (not image hover or drag)
  useUpdateEffect(() => {
    if (props.imgRotateOn === 'trigger') {
      if (props.triggerRotateClockwise) {
        rotate(true);
       } 
       if (props.triggerRotateCounterclockwise) {
        rotate(false);
       }
    }
  }, [props.triggerRotateClockwise, props.triggerRotateCounterclockwise]);

  // Update on reset
  useUpdateEffect(() => {
    if (props.triggerRotationReset && !props.preventRest && angleOffset != 0) { // 0 would indicate initial angle
      resetInProgressRef.current = true;
      resetRotateDegreesRef.current = Math.abs(angleOffset) * 10; // convert to positive number and 10x for degrees
      const clockwise = Math.sign(angleOffset) === -1 ? true : false; // if negative number rotate clockwise
      rotate(clockwise);
    } else {
      resetInProgressRef.current = false;
    }
  }, [props.triggerRotationReset]);

  function getNextImageProps(direction) {
    imgViewingAngleRef.current = direction === 'clockwise' ? imgViewingAngleRef.current - 1 : imgViewingAngleRef.current + 1;
    imgViewingAngleRef.current = imgViewingAngleRef.current > 35 ? 0 : imgViewingAngleRef.current < 0 ? 35 : imgViewingAngleRef.current;
    return Object.assign({}, props.imgConfig, { angle: imgViewingAngleRef.current });
  };
  
  function rotate(clockwise) {
    if (clockwise) {
      imgRotationDirectionRef.current = 'clockwise';
      imgPropsRef.current = getNextImageProps(imgRotationDirectionRef.current);
      setAngleOffset(prevAngleOffset => prevAngleOffset === 35 ? 0 : prevAngleOffset + 1);
    } else {
      imgRotationDirectionRef.current = 'counterClockwise';
      imgPropsRef.current = getNextImageProps(imgRotationDirectionRef.current);
      setAngleOffset(prevAngleOffset => prevAngleOffset === -35 ? 0 : prevAngleOffset - 1);
    }
  };

  function drag(e) {
    e.preventDefault();
    e.stopPropagation();
    const offsetX = e.pageX || e.touches[0].pageX;
    const direction = offsetX < downStartPointRef.current ? 'left' : 'right';
    if (direction === 'left' && offsetX < downStartPointRef.current - 10 ) {
      downStartPointRef.current = offsetX;
      rotate(true);
    }

    if (direction === 'right' && offsetX > downStartPointRef.current + 10 ) {
      downStartPointRef.current = offsetX;
      rotate(false);
    }

    if (props.imgRotateOn === 'drag') {
      if (props.trackingId && props.trackingId == 'range-card') {
        fireGTMTrackingEvent({
          name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
          componentName: props.baseModelName ? RANGE_GTM_TRACKING_VALUES.componentName.gradeRange + '-' + props.baseModelName : RANGE_GTM_TRACKING_VALUES.componentName.modelRange,
          componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
          action: RANGE_GTM_TRACKING_VALUES.action.spinCar,
          label: props.modelName
        })
      }
    }
    
    publish('kc-image-rotation-active');
  };

  function down(e) {
    const offsetX = e.pageX || e.touches[0].pageX;
    downRef.current = true;
    downStartPointRef.current = offsetX;
  };

  function up() {
    downRef.current = false;
  };

  function move(e) {
    if (downRef.current) { // is being dragged with mouse
      drag(e);
    }
  };

  function mouseEnter() {
    rotate(props.imgRotationDirection === 'clockwise');
  };

  function mouseLeave() {
    rotate(props.imgRotationDirection !== 'clockwise');
  };

  return (
    <div className={`${block} c-loading-spinner ${props.imgRotateOn === 'drag' ? block + '__drag' : ''}`} 
    onMouseEnter={props.imgRotateOn === 'hover' ? mouseEnter : null} 
    onMouseLeave={props.imgRotateOn === 'hover' ? mouseLeave : up} 
    onMouseDown={down}
    onMouseUp={up}
    onMouseMove={props.imgRotateOn === 'drag' ? move : null}
    onTouchStart={down}
    onTouchEnd={up}
    onTouchMove={props.imgRotateOn === 'drag' ? drag : null}
    >
      <LazyCCISImage {...imgPropsRef.current} loadAndCacheAngles={'all'} />
    </div>
  )
};

ImageRotator.defaultProps = {
  imgRotationDirection : 'clockwise',
  triggerRotationReset: false
};

ImageRotator.propTypes = {
  imgConfig: propTypes.object.isRequired,
  imgRotateOn: propTypes.string.isRequired,
  imgRotationDegrees: propTypes.number,
  imgRotationSpeed: propTypes.number,
  triggerRotateClockwise: propTypes.bool,
  triggerRotateCounterclockwise: propTypes.bool,
  triggerRotationReset: propTypes.oneOfType([
    propTypes.string,
    propTypes.bool
  ]),
  trackingId: propTypes.string,
  modelName: propTypes.string,
};